// src/App.js
import React, { useState, useEffect } from 'react';
import { useTheme, useMediaQuery, Drawer, ListItemIcon, ListItemButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Snackbar from '@mui/material/Snackbar';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloseIcon from '@mui/icons-material/Close';
import { Home as HomeIcon } from '@mui/icons-material';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
} from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  TextField,
  Box,
  Paper,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  alpha,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CarRental as CarRentalIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  LocalGasStation as FuelIcon,
  Search as SearchIcon,
  Handshake as HandshakeIcon,
  DirectionsCar as BodyTypeIcon,
  Speed as SpeedIcon,
  Settings as SettingsIcon,
  Inventory as InventoryIcon,
  Message as MessageIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

// Modern theme with automotive-inspired colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#1a3e72', // Deep blue
      light: '#4d6a9b',
      dark: '#001747',
    },
    secondary: {
      main: '#e53935', // Vibrant red
      light: '#ff6f60',
      dark: '#ab000d',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2d3748',
      secondary: '#4a5568',
    },
    adminPrimary: {
      main: '#d81b60', // Pink accent for admin
      light: '#ff5c8d',
      dark: '#a00037',
    },
    adminSecondary: {
      main: '#8e24aa', // Purple accent for admin
      light: '#c158dc',
      dark: '#5c007a',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
    subtitle1: {
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '0.5px',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          padding: '8px 20px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function SnackbarAlert({ open, message, severity = 'info', onClose }) {
  return (
    <Snackbar open={open} autoHideDuration={4000} onClose={onClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert severity={severity} onClose={onClose} variant="standard" sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
}


// Header Component
function Header() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const navLinks = [
    { label: 'Home', icon: <HomeIcon />, to: '/' },
    { label: 'Listings', icon: <InventoryIcon />, to: '/listings' },
    { label: 'Admin Dashboard', icon: <SettingsIcon />, to: '/admin' },
  ];

  const drawerContent = (
    <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
      <List>
        {navLinks.map(({ label, icon, to }) => (
          <ListItem button key={label} component={Link} to={to}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar position="static" color="default" elevation={0}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center">
          <CarRentalIcon color="primary" sx={{ fontSize: 32, mr: 1 }} />
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            FastLaneAuto
          </Typography>
        </Box>

        {isMobile ? (
          <>
            <IconButton edge="end" color="inherit" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
              {drawerContent}
            </Drawer>
          </>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            {navLinks.map(({ label, icon, to }) => (
              <Button
                key={label}
                component={Link}
                to={to}
                startIcon={icon}
                color="inherit"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        py: 4,
        backgroundColor: 'primary.main',
        color: 'white',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4} alignItems="flex-start">
          {/* Logo & About */}
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" mb={2}>
              <CarRentalIcon sx={{ fontSize: 32, mr: 1 }} />
              <Typography variant="h6" fontWeight={700}>
                FastLaneAuto
              </Typography>
            </Box>
            <Typography variant="body2">
              Your trusted partner for premium vehicles. We connect buyers with sellers of quality automobiles.
            </Typography>
          </Grid>

          {/* Quick Links - Buttons Side by Side */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Quick Links
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button component={Link} to="/" color="inherit" sx={{ px: 2 }}>
                Home
              </Button>
              <Button component={Link} to="/listings" color="inherit" sx={{ px: 2 }}>
                Listings
              </Button>
              <Button component={Link} to="/admin" color="inherit" sx={{ px: 2 }}>
                Admin
              </Button>
            </Box>
          </Grid>

          {/* Contact Us - Right Aligned on Desktop */}
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Contact Us
            </Typography>
            <Typography variant="body2">
              Email: dealershiptivities@gmail.com
            </Typography>
            <Typography variant="body2">
              Phone: (604) 854-0294
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, backgroundColor: 'rgba(255,255,255,0.1)' }} />

        <Typography variant="body2" align="center">
          © {new Date().getFullYear()} FastLaneAuto. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}


// ImageCarousel Component
function ImageCarousel({ images, height = 400 }) {
  const [index, setIndex] = useState(0);
  const next = () => setIndex((prev) => (prev + 1) % images.length);
  const prev = () => setIndex((prev) => (prev - 1 + images.length) % images.length);
  
  return (
    <Box position="relative" sx={{ borderRadius: '10px', overflow: 'hidden' }}>
      <CardMedia 
        component="img" 
        height={height} 
        image={images[index]} 
        alt="Car"
        sx={{ 
          objectFit: 'cover',
          width: '100%',
        }}
      />
      {images.length > 1 && (
        <>
          <IconButton
            onClick={prev}
            sx={{
              position: 'absolute',
              top: '50%',
              left: 16,
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={next}
            sx={{
              position: 'absolute',
              top: '50%',
              right: 16,
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {images.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: i === index ? 'primary.main' : 'rgba(255,255,255,0.5)',
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

// CarModal Component
function CarModal({ open, onClose, car, backendUrl, showAlert }) {
  const [inquiry, setInquiry] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleInputChange = (e) =>
    setInquiry({ ...inquiry, [e.target.name]: e.target.value });

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiry.name || !inquiry.email || !inquiry.phone || !inquiry.message) {
      alert('Please fill out all fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${backendUrl}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          carId: car.id,
          carTitle: car.title,
          ...inquiry,
        }),
      });
      if (res.ok) {
        showAlert('Inquiry submitted successfully!', 'success');
        setInquiry({ name: '', email: '', phone: '', message: '' });
        onClose();
      } else {
        showAlert('Error submitting inquiry.', 'error');
      }
    } catch (error) {
      console.error('Inquiry error:', error);
      showAlert('Failed to submit inquiry.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setFullscreenImage(true);
  };

  const handleFullscreenClose = () => setFullscreenImage(false);

  const handleFullscreenPrev = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev - 1 + car.images.length) % car.images.length);
  };

  const handleFullscreenNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev + 1) % car.images.length);
  };

  if (!car) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 600,
          }}
        >
          {car.title}
        </DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1, flexWrap: 'wrap' }}>
  <Chip 
    label={`VIN: ${car.vin || 'N/A'}`} 
    variant="outlined" 
    color="primary" 
    sx={{ fontWeight: 500 }} 
  />
  <Chip 
    label={`Stock #: ${car.vin ? car.vin.slice(-6) : 'N/A'}`} 
    variant="outlined" 
    color="secondary" 
    sx={{ fontWeight: 500 }} 
  />
</Box>


        <DialogContent dividers sx={{ pt: 3 }}>
          <Box>
            {car.images && car.images.length > 0 ? (
              <Box sx={{ cursor: 'zoom-in' }} onClick={() => handleImageClick(0)}>
                <ImageCarousel
                  images={car.images}
                  height={300}
                  currentIndex={0}
                  onImageClick={() => handleImageClick(0)}
                />
              </Box>
            ) : (
              <CardMedia
                component="img"
                height="300"
                image="https://via.placeholder.com/700x300?text=No+Image"
                alt={car.title}
                sx={{ borderRadius: '10px', mb: 3 }}
              />
            )}

            <Typography variant="h5" color="text.primary" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
              {car.price}
            </Typography>

            <Typography variant="body1" color="text.secondary" gutterBottom>
              {car.description}
            </Typography>

            <Grid container spacing={2} mt={3}>
              <Grid item xs={6} sm={4}>
                <Box display="flex" alignItems="center">
                  <CalendarIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Year:</strong> {car.year || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box display="flex" alignItems="center">
                  <SpeedIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Kilometres:</strong> {car.kilometres || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box display="flex" alignItems="center">
                  <FuelIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Fuel:</strong> {car.fuelType || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box display="flex" alignItems="center">
                  <BodyTypeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Body:</strong> {car.bodyType || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Box display="flex" alignItems="center">
                  <SettingsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Engine:</strong> {car.engine || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box mt={5}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Contact Seller
              </Typography>
              <form onSubmit={handleInquirySubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Your Name"
                      name="name"
                      value={inquiry.name}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Address"
                      name="email"
                      type="email"
                      value={inquiry.email}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Phone Number"
                      name="phone"
                      value={inquiry.phone}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Your Message"
                      name="message"
                      value={inquiry.message}
                      onChange={handleInputChange}
                      fullWidth
                      multiline
                      rows={4}
                      required
                      variant="outlined"
                      placeholder="I'm interested in this vehicle. Please contact me with more details."
                    />
                  </Grid>
                </Grid>
                <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                  <Button onClick={onClose} variant="outlined" disabled={submitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    startIcon={<MessageIcon />}
                  >
                    {submitting ? 'Sending...' : 'Send Inquiry'}
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {fullscreenImage && car.images && car.images.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={handleFullscreenClose}
        >
          <IconButton
            onClick={handleFullscreenPrev}
            sx={{
              position: 'absolute',
              left: 16,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box
            sx={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={car.images[currentImageIndex]}
              alt={`Car ${currentImageIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>

          <IconButton
            onClick={handleFullscreenNext}
            sx={{
              position: 'absolute',
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <ArrowForwardIcon />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              bottom: 40,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {car.images.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === currentImageIndex ? 'primary.main' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
              />
            ))}
          </Box>

          <IconButton
            onClick={handleFullscreenClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </>
  );
}

function Listings({ cars, onSelectCar }) {
  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Typography
        variant="h3"
        align="center"
        gutterBottom
        sx={{ mb: 6, fontWeight: 700 }}
      >
        All Listings
      </Typography>

      {cars.length === 0 ? (
        <Alert severity="info">No listings available at the moment.</Alert>
      ) : (
        <Grid container spacing={4} justifyContent="center">
          {cars.map((car) => (
            <Grid item key={car.id}>
              <Box sx={{ width: 300 }}>
  <Card
    sx={{
      height: 360,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      borderRadius: 3,
      boxShadow: 2,
      transition: 'transform 0.2s ease-in-out',
      cursor: 'pointer',
      '&:hover': {
        transform: 'scale(1.01)',
        boxShadow: 4,
      },
    }}
    onClick={() => onSelectCar(car)}
  >
    <CardMedia
      component="img"
      height="150"
      image={car.images?.[0] || 'https://via.placeholder.com/400x260?text=No+Image'}
      alt={car.title}
      sx={{ objectFit: 'cover' }}
    />

    <CardContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        pt: 1.5,
        pb: 1.5,
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {car.title}
      </Typography>

      <Box display="flex" alignItems="center" mb={1}>
        <CalendarIcon color="action" sx={{ fontSize: 16, mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {car.year}
        </Typography>
        <Box mx={1}>•</Box>
        <SpeedIcon color="action" sx={{ fontSize: 16, mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {car.kilometres}
        </Typography>
      </Box>

      {/* Show description */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 1,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {car.description}
      </Typography>

      <Box sx={{ flexGrow: 1 }} />

      <Typography
        variant="h6"
        color="primary"
        sx={{ mt: 'auto', fontWeight: 700 }}
      >
        {car.price}
      </Typography>
    </CardContent>
  </Card>
</Box>

            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

// Home Component
function Home({ cars, onSelectCar }) {
  const listingsRef = React.useRef(null);

  const scrollToListings = () => {
    listingsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box>
      {/* Hero Section */}
      {/* Hero Section */}
<Box
  sx={{
    position: 'relative',
    width: '100%',
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    px: 3,
    py: 8,
    backgroundImage: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.2)), url("/hero.jpg")', // Reduced opacity
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: { xs: 'scroll', md: 'fixed' }, // Creates parallax effect
    color: 'white',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)', // Gradient overlay
      zIndex: 1,
    },
  }}
>
  <Container 
    maxWidth="md"
    sx={{
      position: 'relative',
      zIndex: 2, // Ensures content stays above overlay
    }}
  >
    <Typography
      variant="h1"
      sx={{
        mb: 3,
        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
      }}
    >
      Find Your Perfect Vehicle
    </Typography>
    
    <Typography
      variant="h5"
      component="p"
      sx={{
        mb: 5,
        fontWeight: 400,
        color: 'rgba(255,255,255,0.9)',
      }}
    >
      Browse our premium selection of vehicles and find the perfect match for your needs
    </Typography>
    
    <Button
      variant="contained"
      color="primary"
      size="large"
      onClick={scrollToListings}
      sx={{
        px: 6,
        py: 1.5,
        fontSize: '1.1rem',
        backdropFilter: 'blur(2px)', // Subtle glass effect
        backgroundColor: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.2)',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.2)',
        }
      }}
    >
      View Vehicles
    </Button>
  </Container>
</Box>

      {/* Features Section */}
<Box sx={{ py: 8, backgroundColor: 'background.default' }}>
  <Container maxWidth="lg">
    <Typography
      variant="h3"
      align="center"
      gutterBottom
      sx={{ mb: 6, fontWeight: 700 }}
    >
      Why Choose Us
    </Typography>

    <Grid container spacing={4} justifyContent="center">
      {[
        {
          icon: <CarRentalIcon sx={{ fontSize: 50 }} color="primary" />,
          title: 'Wide Selection',
          description: 'Hundreds of vehicles to choose from across all makes and models.',
        },
        {
          icon: <PersonIcon sx={{ fontSize: 50 }} color="primary" />,
          title: 'Trusted Sellers',
          description: 'All our sellers are verified to ensure you get quality vehicles.',
        },
        {
          icon: <SpeedIcon sx={{ fontSize: 50 }} color="primary" />,
          title: 'Detailed Listings',
          description: 'Comprehensive vehicle information with multiple photos.',
        },
        {
          icon: <SupportAgentIcon sx={{ fontSize: 50 }} color="primary" />,
          title: 'Dedicated Support',
          description: '24/7 customer service to guide you through every step of your purchase.',
        },
      ].map((feature, index) => (
        <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex' }}>
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              borderRadius: 3,
              backgroundColor: 'white',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              },
            }}
          >
            <Box mb={2}>{feature.icon}</Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {feature.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {feature.description}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Container>
</Box>


{/* Car Listings Section */}
<Box sx={{ pt: 8, pb: 4 }} ref={listingsRef}>
  <Container maxWidth="xl">
    <Typography 
      variant="h3" 
      align="center" 
      gutterBottom
      sx={{
        mb: 6,
        fontWeight: 700,
      }}
    >
      Featured Vehicles
    </Typography>

    {cars.length === 0 ? (
      <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
        No vehicles currently available. Please check back later.
      </Alert>
    ) : (
      <>
        <Grid container spacing={4} justifyContent="center">
          {cars.slice(0, 8).map((car) => (
            <Grid item key={car.id}>
              <Box sx={{ width: 300 }}>
                <Card
                  sx={{
                    height: 360,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 3,
                    boxShadow: 2,
                    transition: 'transform 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.01)',
                      boxShadow: 4,
                    },
                  }}
                  onClick={() => onSelectCar(car)}
                >
                  <CardMedia
                    component="img"
                    height="150"
                    image={car.images?.[0] || 'https://via.placeholder.com/400x260?text=No+Image'}
                    alt={car.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexGrow: 1,
                      pt: 1.5,
                      pb: 1.5,
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {car.title}
                    </Typography>

                    <Box display="flex" alignItems="center" mb={1}>
                      <CalendarIcon color="action" sx={{ fontSize: 16, mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {car.year}
                      </Typography>
                      <Box mx={1}>•</Box>
                      <SpeedIcon color="action" sx={{ fontSize: 16, mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {car.kilometres}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {car.description}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{ mt: 'auto', fontWeight: 700 }}
                    >
                      {car.price}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box mt={5} display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            size="large"
            component={Link}
            to="/listings"
            startIcon={<InventoryIcon />}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            View All Listings
          </Button>
        </Box>
      </>
    )}
  </Container>
</Box>

{/* How It Works Section */}
<Box sx={{ py: 10, backgroundColor: 'background.default' }}>
  <Container maxWidth="lg">
    <Typography
      variant="h3"
      align="center"
      gutterBottom
      sx={{ mb: 8, fontWeight: 700 }}
    >
      How It Works
    </Typography>

    <Grid container spacing={4} justifyContent="center">
      {[
        {
          icon: <SearchIcon sx={{ fontSize: 30 }} />,
          title: 'Browse Listings',
          description: 'Explore a wide range of vehicles from trusted sellers.',
        },
        {
          icon: <MessageIcon sx={{ fontSize: 30 }} />,
          title: 'Send an Inquiry',
          description: 'Contact the seller with your questions and interest.',
        },
        {
          icon: <HandshakeIcon sx={{ fontSize: 30 }} />,
          title: 'Make the Deal',
          description: 'Connect with the seller and close the deal confidently.',
        },
      ].map((step, index) => (
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          key={index}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 4,
              p: 4,
              textAlign: 'center',
              width: '100%',
              maxWidth: 300,
              height: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
              },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              {step.icon}
            </Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Step {index + 1}: {step.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step.description}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Container>
</Box>





    </Box>
  );
}

// Admin Component
function Admin({ addCar, fetchCars, backendUrl, showAlert }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [car, setCar] = useState({
    title: '', description: '', price: '', make: '', model: '', year: '', kilometres: '', bodyType: '', engine: '', fuelType:'', vin: ''
  });
  const [images, setImages] = useState(['']);
  const [cars, setCars] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [editingCar, setEditingCar] = useState(null);


  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setAuthenticated(true);
        fetchCarsAndInquiries();
      } else {
        showAlert('Incorrect password', 'error');
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const fetchCarsAndInquiries = async () => {
    try {
      const resCars = await fetch(`${backendUrl}/api/cars`, { credentials: 'include' });
      const carsData = await resCars.json();
      setCars(carsData);

      const resInquiries = await fetch(`${backendUrl}/api/inquiries`, { credentials: 'include' });
      const inquiriesData = await resInquiries.json();
if (Array.isArray(inquiriesData)) {
  setInquiries(inquiriesData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
} else {
  console.warn("Inquiries fetch failed or returned unexpected format:", inquiriesData);
  setInquiries([]);
}

    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this car?')) return;
    try {
      const res = await fetch(`${backendUrl}/api/cars/${carId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        showAlert('Car deleted successfully', 'success');
        fetchCarsAndInquiries();
      } else {
        showAlert('Failed to delete car', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleChange = (e) => setCar({ ...car, [e.target.name]: e.target.value });
  const handleImageChange = (index, value) => {
    const newImages = [...images];
    newImages[index] = value;
    setImages(newImages);
  };
  const addImageField = () => setImages([...images, '']);
  const removeImageField = (index) => setImages(images.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const filteredImages = images.map((url) => url.trim()).filter((url) => url !== '');
      const carPayload = { ...car, images: filteredImages };
  
      const method = editingCar ? 'PUT' : 'POST';
      const url = editingCar 
        ? `${backendUrl}/api/cars/${editingCar}`
        : `${backendUrl}/api/cars`;
  
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(carPayload),
      });
  
      if (res.ok) {
        showAlert(`Car ${editingCar ? 'updated' : 'added'} successfully`, 'success');
        setCar({ title: '', description: '', price: '', make: '', model: '', year: '', kilometres: '', bodyType: '', engine: '', fuelType: '' });
        setImages(['']);
        setEditingCar(null);
        fetchCarsAndInquiries();
      } else {
        showAlert(`Error ${editingCar ? 'updating' : 'adding'} car`, 'error');
      }
    } catch (error) {
      console.error('Submit error:', error);
      showAlert('Submission failed', 'error');
    }
  };  
  if (!authenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: '10px',
          }}
        >
          <Box textAlign="center" mb={4}>
            <LockIcon color="primary" sx={{ fontSize: 50 }} />
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              Admin Login
            </Typography>
          </Box>
          <form onSubmit={handleLogin}>
            <TextField 
              label="Password" 
              type="password" 
              fullWidth 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              sx={{ mb: 3 }}
              variant="outlined"
            />
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth
              size="large"
            >
              Login
            </Button>
          </form>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography 
        variant="h3" 
        gutterBottom 
        sx={{ 
          mb: 4,
          fontWeight: 700,
        }}
      >
        Admin Dashboard
      </Typography>

      <Tabs 
        value={activeTab} 
        onChange={(_, newValue) => setActiveTab(newValue)} 
        sx={{ mb: 3 }}
      >
        <Tab label="Add Vehicle" icon={<CarRentalIcon />} />
        <Tab label="Inventory" icon={<InventoryIcon />} />
        <Tab 
          label={
            <Badge badgeContent={inquiries.length} color="primary">
              Inquiries
            </Badge>
          } 
          icon={<MessageIcon />} 
        />
      </Tabs>

      {activeTab === 0 && (
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Add New Vehicle
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {['title', 'price', 'make', 'model', 'year', 'kilometres', 'bodyType', 'engine', 'fuelType', 'vin'].map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  <TextField
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    name={field}
                    value={car[field]}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={car.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
                  Image URLs
                </Typography>
                <Button 
                  onClick={addImageField} 
                  variant="outlined" 
                  color="primary"
                  sx={{ mb: 2 }}
                >
                  Add Image URL
                </Button>
                {images.map((img, index) => (
                  <Box 
                    key={index} 
                    display="flex" 
                    alignItems="center" 
                    gap={1}
                    sx={{ mb: 2 }}
                  >
                    <TextField
                      label={`Image URL ${index + 1}`}
                      value={img}
                      onChange={(e) => handleImageChange(index, e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                    <IconButton 
                      onClick={() => removeImageField(index)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12}>
              <Button 
  type="submit" 
  variant="contained" 
  color="primary"
  size="large"
  sx={{ mt: 2 }}
>
  {editingCar ? 'Save Changes' : 'Add Vehicle'}
</Button>

              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Current Inventory ({cars.length})
          </Typography>
          <List sx={{ maxHeight: 600, overflow: 'auto' }}>
            {cars.map((car) => (
              <ListItem 
              key={car.id} 
              divider
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <ListItemText 
                  primary={
                    <Typography fontWeight={600}>
                      {car.title}
                    </Typography>
                  } 
                  secondary={
                    <Box component="span" display="flex" alignItems="center" mt={0.5}>
                      <CalendarIcon color="action" sx={{ fontSize: 16, mr: 1 }} />
                      <Typography variant="body2" component="span" mr={2}>
                        {car.year}
                      </Typography>
                      <SpeedIcon color="action" sx={{ fontSize: 16, mr: 1 }} />
                      <Typography variant="body2" component="span">
                        {car.kilometres}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            
              <Typography 
                variant="body1" 
                sx={{ fontWeight: 600, minWidth: 90, textAlign: 'right', mr: 2 }}
              >
                {car.price}
              </Typography>
            
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton 
                  onClick={() => {
                    setCar(car);
                    setImages(car.images || []);
                    setActiveTab(0);
                    setEditingCar(car.id);
                  }}
                  sx={{ color: 'primary.main' }}
                >
                  <EditIcon />
                </IconButton>
            
                <IconButton 
                  onClick={() => handleDeleteCar(car.id)}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </ListItem>
            
            ))}
          </List>
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Customer Inquiries ({inquiries.length})
          </Typography>
          <List sx={{ maxHeight: 600, overflow: 'auto' }}>
            {inquiries.map((inq) => (
              <ListItem 
                key={inq.id} 
                divider
                alignItems="flex-start"
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography fontWeight={600}>
                      {inq.name} • {inq.email} • {inq.phone}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                        <strong>Vehicle:</strong> {inq.carTitle}
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                        {inq.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        display="block" 
                        sx={{ mt: 1, color: 'text.secondary' }}
                      >
                        {new Date(inq.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Container>
  );
}

function App() {
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
  console.log(backendUrl)
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });

const showAlert = (message, severity = 'info') => {
  setAlert({ open: true, message, severity });
};

const handleCloseAlert = () => {
  setAlert({ ...alert, open: false });
};


  const fetchCars = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/cars`, { credentials: 'include' });
      const data = await res.json();
      setCars(data);
    } catch (error) {
      console.error('Error fetching cars:', error);
    }
  };

  useEffect(() => {
    fetchCars();
  }, []);

  const addCar = (car) => setCars([...cars, car]);

  return (
    
    <ThemeProvider theme={theme}>
      <SnackbarAlert 
  open={alert.open} 
  message={alert.message} 
  severity={alert.severity} 
  onClose={handleCloseAlert} 
/>

      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Header />
          
          <Box component="main" sx={{ flexGrow: 1 }}>
          <Routes>
  <Route path="/" element={<Home cars={cars} onSelectCar={setSelectedCar} />} />
  <Route path="/admin" element={<Admin addCar={addCar} fetchCars={fetchCars} backendUrl={backendUrl} showAlert={showAlert} />} />
  <Route path="/listings" element={<Listings cars={cars} onSelectCar={setSelectedCar} />} />
</Routes>

          </Box>
          
          <Footer />
          
          <CarModal
            open={Boolean(selectedCar)}
            onClose={() => setSelectedCar(null)}
            car={selectedCar}
            backendUrl={backendUrl}
            showAlert={showAlert}
          />
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
